import { personDisplayName, type CatalogOptions, type LifecycleState, type Person, type ReadinessState } from '@vigeo/shared';
import { lifecyclePresenters } from './lifecycle.js';

/**
 * The People directory's local model (checkpoint 7B): search, filters, sorting and summary
 * counts over the ONE People response. Everything here is a pure projection — nothing is
 * requested, stored or recomputed — so Board and List cannot disagree about who is shown.
 */

export type DirectoryView = 'board' | 'list';
export const DIRECTORY_VIEW_KEY = 'vigeo-hr.people.view';

export const directorySorts = ['name-asc', 'name-desc', 'stage', 'attention', 'updated'] as const;
export type DirectorySort = (typeof directorySorts)[number];

export const sortPresenters = {
  'name-asc': { label: 'Last name A–Z' },
  'name-desc': { label: 'Last name Z–A' },
  stage: { label: 'Stage' },
  attention: { label: 'Needs attention first' },
  updated: { label: 'Recently updated' },
} as const satisfies Record<DirectorySort, { readonly label: string }>;

/**
 * The readiness sort: verdicts HR must act on first, then the ones that only need chasing.
 *
 * `setup_required` ranks second because it is HR's own to fix and nobody else can move it —
 * an unprofiled person is stuck until someone assigns a profile, whereas a `not_ready` one is
 * already being chased. It must never rank beside `ready`; that would put the people nobody
 * has assessed at the quiet end of the list.
 */
const attentionRank = { needs_review: 0, setup_required: 1, not_ready: 2, ready_with_exceptions: 3, ready: 4 } as const satisfies Record<ReadinessState, number>;

/** Readiness for a person, or null while it is still being derived (or could not be). */
export type ReadinessLookup = (id: string) => ReadinessState | null;

export interface DirectoryFilters {
  /** Search within People only: name, email and employee ID. */
  readonly query: string;
  readonly stage: LifecycleState | 'all';
  /** A job profile key, or 'all'. */
  readonly role: string;
  readonly readiness: ReadinessState | 'all';
}

export const noFilters: DirectoryFilters = { query: '', stage: 'all', role: 'all', readiness: 'all' };

export function isFiltered(filters: DirectoryFilters): boolean {
  return filters.query.trim() !== '' || filters.stage !== 'all' || filters.role !== 'all' || filters.readiness !== 'all';
}

/** Role text from the catalog's labels — never a bare key while the catalog is known. */
export function roleLabel(person: Person, options: CatalogOptions | undefined): string {
  return roleLabelForKeys(person.jobProfileKeys, options);
}

/**
 * The same label from the KEYS alone, for rows that carry a person's profiles without carrying the
 * person — `/api/offers` is one (`OfferRow.jobProfileKeys`). Split out rather than reimplemented so
 * "No role set" and the catalog lookup have exactly one definition; a second copy is how two
 * screens start disagreeing about an unlabelled key.
 */
export function roleLabelForKeys(keys: readonly string[], options: CatalogOptions | undefined): string {
  if (keys.length === 0) return 'No role set';
  if (options === undefined) return '';
  return keys.map((key) => options.jobProfiles.find((p) => p.key === key)?.label ?? key).join(', ');
}

export function matchesQuery(person: Person, query: string): boolean {
  const terms = query.trim().toLowerCase().split(/\s+/).filter((t) => t !== '');
  if (terms.length === 0) return true;
  const haystack = [personDisplayName(person.name), person.name.givenName, person.name.familyName, person.contact.email, person.employeeId ?? '']
    .join(' ')
    .toLowerCase();
  return terms.every((term) => haystack.includes(term));
}

export function filterPeople(people: readonly Person[], filters: DirectoryFilters, readinessOf: ReadinessLookup): Person[] {
  return people.filter(
    (person) =>
      matchesQuery(person, filters.query) &&
      (filters.stage === 'all' || person.lifecycle === filters.stage) &&
      (filters.role === 'all' || person.jobProfileKeys.includes(filters.role)) &&
      (filters.readiness === 'all' || readinessOf(person.id) === filters.readiness),
  );
}

const byName = (a: Person, b: Person) =>
  a.name.familyName.localeCompare(b.name.familyName) || a.name.givenName.localeCompare(b.name.givenName);
const rank = (state: ReadinessState | null) => (state === null ? Object.keys(attentionRank).length : attentionRank[state]);

const comparators = {
  'name-asc': () => byName,
  'name-desc': () => (a: Person, b: Person) => byName(b, a),
  stage: () => (a: Person, b: Person) => lifecyclePresenters[a.lifecycle].order - lifecyclePresenters[b.lifecycle].order || byName(a, b),
  attention: (readinessOf: ReadinessLookup) => (a: Person, b: Person) => rank(readinessOf(a.id)) - rank(readinessOf(b.id)) || byName(a, b),
  updated: () => (a: Person, b: Person) => b.updatedAt.localeCompare(a.updatedAt) || byName(a, b),
} as const satisfies Record<DirectorySort, (readinessOf: ReadinessLookup) => (a: Person, b: Person) => number>;

export function sortPeople(people: readonly Person[], sort: DirectorySort, readinessOf: ReadinessLookup): Person[] {
  return [...people].sort(comparators[sort](readinessOf));
}

export interface DirectorySummary {
  readonly total: number;
  /** Every lifecycle state, in presenter order, with its count — zero included. */
  readonly byStage: readonly { readonly state: LifecycleState; readonly count: number }[];
}

/** Counts that follow from the People response alone. */
export function summarize(people: readonly Person[]): DirectorySummary {
  const states = (Object.keys(lifecyclePresenters) as LifecycleState[]).sort((a, b) => lifecyclePresenters[a].order - lifecyclePresenters[b].order);
  return {
    total: people.length,
    byStage: states.map((state) => ({ state, count: people.filter((p) => p.lifecycle === state).length })),
  };
}

/** Roles present in the response, labelled from the catalog, for the role picker. */
export function rolesPresent(people: readonly Person[], options: CatalogOptions | undefined): readonly { readonly key: string; readonly label: string }[] {
  const keys = [...new Set(people.flatMap((p) => p.jobProfileKeys))];
  return keys
    .map((key) => ({ key, label: options?.jobProfiles.find((p) => p.key === key)?.label ?? key }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

export function readStoredView(): DirectoryView {
  try {
    return window.localStorage.getItem(DIRECTORY_VIEW_KEY) === 'list' ? 'list' : 'board';
  } catch {
    return 'board';
  }
}

export function storeView(view: DirectoryView): void {
  try {
    window.localStorage.setItem(DIRECTORY_VIEW_KEY, view);
  } catch {
    // Session-only when storage is unavailable.
  }
}
